import type { SituationLayer } from "../../api/types";
import { drawBridge } from "../../map/bridgeImages";
import { drawNeutralChip, MISSION_PREFIX, missionFile, NEUTRAL_IMAGE } from "../../map/units/missionImages";
import { SYMBOL_PREFIX, symbolImage } from "../../map/units/symbols";
import { ANNOTATION_PREFIX, annotationImage } from "../../map/annotations/symbolStyle";

/** Images du KMZ, dessinées comme sur la carte : symboles APP-6 des unités (avec leurs désignations), annotations
 *  APP-6 (avec leur amplificateur texte), symboles de mission de l'iPad, ponts intacts ou détruits. Le serveur n'a pas de moteur de symboles : il reçoit ces
 *  images en PNG et les range dans le KMZ ; une image manquante devient un marqueur de la couleur du camp. */

const PIXEL_RATIO = 2;

/** Couche à laquelle appartient une image (seules les couches cochées sont dessinées). */
export function iconLayer(id: string): SituationLayer | null {
  if (id.startsWith(SYMBOL_PREFIX)) return "units";
  if (id.startsWith(ANNOTATION_PREFIX)) return "annotations";
  if (id.startsWith(MISSION_PREFIX)) return "missions";
  if (id === "bridge" || id === "bridge-destroyed") return "bridges";
  return null;
}

function png(image: ImageData): string {
  const canvas = document.createElement("canvas");
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D indisponible");
  ctx.putImageData(image, 0, 0);
  return canvas.toDataURL("image/png");
}

function dataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("Lecture impossible"));
    reader.readAsDataURL(blob);
  });
}

async function draw(id: string): Promise<string | null> {
  if (id.startsWith(SYMBOL_PREFIX)) {
    const [sidc = "", left = "", right = ""] = id.slice(SYMBOL_PREFIX.length).split("|");
    return png(symbolImage(sidc, PIXEL_RATIO, left, right));
  }
  if (id.startsWith(ANNOTATION_PREFIX)) {
    const [sidc = "", amplifier = ""] = id.slice(ANNOTATION_PREFIX.length).split("|");
    return png(annotationImage(sidc, PIXEL_RATIO, amplifier));
  }
  if (id === `${MISSION_PREFIX}${NEUTRAL_IMAGE}`) {
    // Verbe sans image du catalogue (niveau compagnie) : pastille neutre, le verbe est dans le nom du repère.
    const chip = drawNeutralChip("•");
    return chip ? chip.toDataURL("image/png") : null;
  }
  if (id.startsWith(MISSION_PREFIX)) {
    const res = await fetch(missionFile(id.slice(MISSION_PREFIX.length)));
    return res.ok ? dataUrl(await res.blob()) : null;
  }
  if (id === "bridge" || id === "bridge-destroyed") return png(drawBridge(id === "bridge-destroyed", PIXEL_RATIO));
  return null;
}

/** Images PNG (URL data:) des identifiants demandés, pour les couches cochées. */
export async function kmzIcons(ids: string[], layers: SituationLayer[]): Promise<Record<string, string>> {
  const wanted = ids.filter((id) => {
    const layer = iconLayer(id);
    return layer !== null && layers.includes(layer);
  });
  const out: Record<string, string> = {};
  await Promise.all(
    wanted.map(async (id) => {
      try {
        const image = await draw(id);
        if (image) out[id] = image;
      } catch {
        /* image impossible à dessiner : marqueur de couleur dans le KMZ */
      }
    }),
  );
  return out;
}
